import { Injectable, effect, signal, untracked } from '@angular/core';

import { AppAuthService } from '@services/core/auth/app-auth.service';
import { RegionService } from '@app/areas/auth/services/region.service';
import { WorkspaceSwitcherService } from '@app/shared/services/core/workspace-switcher/workspace-switcher.service';
import { IdentityUserRegion } from '@app/areas/users/models/identity-user.interface';

@Injectable({
  providedIn: 'root',
})
export class SessionBootstrapService {
  readonly ready$ = signal(false);
  readonly bootstrapping$ = signal(false);

  private resolvedUserId: string | null = null;
  private resolvingRegion = false;
  private seededProfileRegion = false;

  constructor(
    private readonly auth: AppAuthService,
    private readonly workspaceSwitcher: WorkspaceSwitcherService,
    private readonly regionService: RegionService,
  ) {
    effect(() => {
      const user = this.auth.user$();
      const userId = user?.identityProfile?.id ?? null;
      const profileRegion = user?.identityProfile?.region ?? null;
      const currentRegion = this.regionService.selectedRegion$();
      const workspaceLoading = this.workspaceSwitcher.loading$();
      const loadedUserId = this.workspaceSwitcher.loadedUserId$();
      const selectedWorkspace = this.workspaceSwitcher.selectedWorkspace$();
      const ready = untracked(() => this.ready$());
      const bootstrapping = untracked(() => this.bootstrapping$());

      console.debug('[SessionBootstrap] tick', {
        userId,
        profileRegion,
        currentRegion,
        workspaceLoading,
        loadedUserId,
        ready,
        bootstrapping,
        selectedWorkspaceId: selectedWorkspace?.id ?? null,
      });

      if (!userId) {
        this.reset();
        console.debug('[SessionBootstrap] reset: no authenticated user');
        return;
      }

      if (this.resolvedUserId !== userId) {
        this.resolvedUserId = userId;
        this.ready$.set(false);
        this.resolvingRegion = false;
        this.seededProfileRegion = false;
        console.debug('[SessionBootstrap] new user detected', { userId });
      }

      if (workspaceLoading || loadedUserId !== userId) {
        if (!this.bootstrapping$()) {
          this.bootstrapping$.set(true);
        }
        console.debug('[SessionBootstrap] waiting for workspace switcher', {
          userId,
          workspaceLoading,
          loadedUserId,
        });
        return;
      }

      void this.resolveSessionRegion({
        userId,
        currentRegion,
        profileRegion,
        workspaceRegion: (selectedWorkspace?.region ?? null) as IdentityUserRegion | null,
      });
    });
  }

  private async resolveSessionRegion(params: {
    userId: string;
    currentRegion: IdentityUserRegion | null;
    profileRegion: IdentityUserRegion | null;
    workspaceRegion: IdentityUserRegion | null;
  }): Promise<void> {
    const desiredRegion = await this.pickAvailableRegion(
      [
        this.regionService.adminSelectedRegion$(),
        params.workspaceRegion,
        params.currentRegion,
        params.profileRegion,
      ],
    );

    if (!desiredRegion) {
      if (!this.ready$()) {
        this.ready$.set(true);
      }
      if (this.bootstrapping$()) {
        this.bootstrapping$.set(false);
      }
      this.resolvingRegion = false;
      this.seededProfileRegion = true;
      console.debug('[SessionBootstrap] ready: no region to resolve', { userId: params.userId });
      return;
    }

    if (params.currentRegion !== desiredRegion) {
      if (this.resolvingRegion) {
        return;
      }

      this.resolvingRegion = true;
      console.debug('[SessionBootstrap] applying desired region', {
        userId: params.userId,
        desiredRegion,
        currentRegion: params.currentRegion,
      });
      this.regionService.setBootstrapRegion(desiredRegion);
    }

    this.seededProfileRegion = params.profileRegion !== null;
    this.resolvingRegion = false;
    if (!this.ready$()) {
      this.ready$.set(true);
    }
    if (this.bootstrapping$()) {
      this.bootstrapping$.set(false);
    }
    console.debug('[SessionBootstrap] ready', {
      userId: params.userId,
      desiredRegion,
    });
  }

  private async pickAvailableRegion(
    candidates: Array<IdentityUserRegion | null>,
  ): Promise<IdentityUserRegion | null> {
    for (const region of candidates) {
      if (!region) {
        continue;
      }

      const isAvailable = await this.regionService.canReachRegion(region);
      if (isAvailable) {
        return region;
      }
    }

    return null;
  }

  private reset(): void {
    this.resolvedUserId = null;
    this.resolvingRegion = false;
    if (this.ready$()) {
      this.ready$.set(false);
    }
    if (this.bootstrapping$()) {
      this.bootstrapping$.set(false);
    }
    console.debug('[SessionBootstrap] reset complete');
  }
}
